import classNames from 'classnames';
import React, { useState, memo, useEffect, useMemo, useRef } from 'react';

import ClearSearchButton from '../../../components/ClearSearchButton';
import type { RBACGroup } from '../../../types';
import { sortByDisplayName } from '../sortByDisplayName';

type Props = {
  items: RBACGroup[];
  selectedId?: string;
  onSelect: (value: RBACGroup) => void;
  enabled?: boolean;
  // For "add" boxes (no persistent selection): clear the input after a pick so the
  // next one can be queued. Defaults off, preserving the select-and-fill behaviour.
  clearOnSelect?: boolean;
  placeholder?: string;
  // Invoked by the in-input X button after the text is cleared — lets the caller
  // also drop the current selection (e.g. navigate back to the unselected route).
  onClear?: () => void;
};

function Autocomplete({
  items,
  selectedId,
  onSelect,
  enabled,
  clearOnSelect,
  placeholder,
  onClear,
}: Props) {
  // focus:outline-none drops daisyUI's focus ring — by design the box already
  // signals focus via its open dropdown, and the ring read as noise here.
  // pr-9 leaves room for the clear (X) button overlaid on the right.
  const inputClassName = classNames('input w-full pr-9 focus:outline-none');

  const [value, setValue] = useState('');

  // groupList is the filtered list of grpi[s], initially it is the same as items
  const [groupList, setGroupList] = useState<RBACGroup[]>(items);

  // Fill the input with the selected group's name when the selected user changes
  useEffect(() => {
    const selectedUser = items.find((user) => user.id === selectedId);
    if (selectedUser) {
      setValue(selectedUser.displayName!);
    }
  }, [selectedId, items]);

  // Filter userList based on entered value
  useEffect(() => {
    setGroupList(
      items.filter((item) => item.displayName?.toLowerCase().includes(value.toLowerCase()))
    );
  }, [value, items]);

  // Memoized so downstream render only re-sorts when the filtered list actually changes.
  const sortedList = useMemo(() => sortByDisplayName(groupList), [groupList]);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // The daisyUI .dropdown reveals its content purely via CSS :focus-within, so we can't
  // both keep the input focused after a pick AND have the list stay closed unless we gate
  // whether the content is rendered at all. isOpen is that gate: it starts true (the list
  // is rendered but stays CSS-hidden until focus, as before) and is only force-closed after
  // a pick, staying closed until the next focus/keystroke.
  const [isOpen, setIsOpen] = useState(true);
  // Picking refocuses the input to keep it ready for the next entry; that refocus fires
  // onFocus, which would otherwise re-open the list we just closed. This one-shot flag
  // swallows exactly that programmatic refocus.
  const suppressOpenRef = useRef(false);

  // Close the list but keep the input focused so the next entry can be typed straight away.
  // The suppress flag stops the refocus below from immediately re-opening the list.
  const closeAndRefocus = () => {
    suppressOpenRef.current = true;
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleClick = (item: RBACGroup) => {
    onSelect(item);
    if (clearOnSelect) setValue('');
    closeAndRefocus();
  };

  // Select all text when input is focused, and open the list — unless this focus came from
  // the programmatic refocus after a pick.
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (suppressOpenRef.current) {
      suppressOpenRef.current = false;
      return;
    }
    e.target.select();
    setIsOpen(true);
  };

  // Arrow down from the input jumps into the first result; Escape closes the list.
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      listRef.current?.querySelector<HTMLElement>('li')?.focus();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Arrows move between options; arrow up from the first returns to the input
  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLLIElement>, item: RBACGroup) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(item);
    } else if (e.key === 'Escape') {
      closeAndRefocus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      (e.currentTarget.nextElementSibling as HTMLElement | null)?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const previous = e.currentTarget.previousElementSibling as HTMLElement | null;
      if (previous) {
        previous.focus();
      } else {
        inputRef.current?.focus();
      }
    }
  };

  return (
    <div className="dropdown w-full">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className={inputClassName}
          placeholder={enabled ? (placeholder ?? 'Search for a Role group') : 'Select a user first'}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setIsOpen(true);
          }}
          tabIndex={0}
          onFocus={handleFocus}
          onKeyDown={handleInputKeyDown}
          disabled={!enabled}
        />
        {value && enabled && (
          <ClearSearchButton
            onClick={() => {
              setValue('');
              onClear?.();
              inputRef.current?.focus();
            }}
          />
        )}
      </div>
      {!isOpen || groupList.length === 0 ? (
        <div></div>
      ) : (
        <div className="w-full dropdown-content border border-base-200 bg-base-100 top-14 max-h-[50rem] overflow-auto flex-col rounded-md">
          <ul className="menu menu-sm w-full" role="listbox" ref={listRef}>
            {sortedList.map((item) => {
              return (
                <li
                  key={item.id}
                  // 0, not index+1: positive tabindex sorts before the search
                  // input globally, making the list unreachable by Tab
                  tabIndex={0}
                  role="option"
                  aria-selected={item.id === selectedId}
                  onClick={() => {
                    handleClick(item);
                  }}
                  onKeyDown={(e) => handleOptionKeyDown(e, item)}
                  className="group border-b last:border-b-0 border-b-base-200 w-full focus:outline-none"
                >
                  {/* Hover/keyboard-focus paints the option primary (purple in the
                      dark theme), matching the active nav button in the header bar. */}
                  <div className="flex flex-col items-start gap-0 w-full group-hover:bg-primary group-hover:text-primary-content group-focus:bg-primary group-focus:text-primary-content">
                    <div className="flex flex-row w-full justify-between align-center">
                      <div className="truncate">{item.displayName}</div>
                    </div>
                    <div className="text-xs w-full truncate" title={item.description}>
                      {item.description}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default memo(Autocomplete);
